import React from 'react';
import { CalendarOff } from 'lucide-react';
import { COLOR_SETS } from '../lib/constants.js';
import { DAY_START, HOUR_HEIGHT, MIN_ENTRY_PX } from '../lib/calendar.js';
import { fmtDur, fmtTime } from '../lib/time.js';
import { ProjectLabel } from './ui.jsx';

function durationHM(hours) {
  const total = Math.round(Math.max(0, hours) * 60);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

// Absence (vacation, sick leave, doctor…) is not work, so it doesn't wear its
// project's colour: grey hatching with a solid grey edge (calendar.css
// `.cal-absence`), solid so it never reads as a dashed Outlook event.
export function entryAppearance(project, fallbackColor) {
  if (project?.absence) return { className: 'cal-absence', colors: {} };
  const color = COLOR_SETS[project?.color || fallbackColor] || COLOR_SETS.teal;
  return {
    className: '',
    colors: {
      background: color.soft,
      borderColor: color.bg,
      color: color.text,
    },
  };
}

// The project line of an entry. An absence one keeps the grey of its block
// (no project-coloured code chip) and carries the absence glyph.
export function EntryProjectLabel({ project }) {
  if (!project?.absence) return <ProjectLabel project={project} />;
  return (
    <span className="project-label cal-absence-label">
      <CalendarOff size={12} aria-hidden="true" />
      <span className="mini-code">{project.code}</span>
      <span className="truncate">{project.name}</span>
    </span>
  );
}

export function CalendarEntry({
  entry,
  project,
  drag,
  onOpen,
  onMoveStart,
  onResizeStart,
  onResizeStartTop,
  onContextMenu,
  col = 0,
  cols = 1,
  hourHeight = HOUR_HEIGHT,
}) {
  const appearance = entryAppearance(project, entry.color);
  const active = drag?.entry?.id === entry.id;
  const start = active ? drag.start : entry.start;
  const end = active ? drag.end : entry.end;
  const projectData = project || {
    code: entry.projectCode,
    name: entry.projectName,
    color: entry.color,
  };
  const projectText = [projectData.code, projectData.name]
    .filter(Boolean)
    .join(' ');
  const tooltip = `${projectText}${
    entry.description ? ` — ${entry.description}` : ''
  } ${durationHM(end - start)}h (${fmtTime(start)} - ${fmtTime(end)})`;

  return (
    <button
      data-calendar-entry
      draggable={false}
      className={`cal-entry ${appearance.className}`.trim()}
      onMouseDown={(event) => onMoveStart(event, entry)}
      onClick={(event) => {
        event.stopPropagation();
        onOpen();
      }}
      onContextMenu={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onContextMenu?.(event, entry);
      }}
      style={{
        top: (start - DAY_START) * hourHeight,
        height: Math.max(MIN_ENTRY_PX, (end - start) * hourHeight),
        left: cols > 1 ? `${(col / cols) * 100}%` : 0,
        width: cols > 1 ? `calc(${(1 / cols) * 100}% - 3px)` : undefined,
        ...appearance.colors,
        boxShadow: active ? '0 10px 26px rgba(15,23,42,.18)' : undefined,
      }}
    >
      <span
        className="cal-entry-handle cal-entry-handle-top"
        onMouseDown={(event) => onResizeStartTop(event, entry)}
      />
      <div className="cal-entry-body">
        <div className="cal-entry-top">
          {entry.description ? (
            <span className="cal-entry-desc">{entry.description}</span>
          ) : (
            <EntryProjectLabel project={projectData} />
          )}
          <span className="cal-entry-dur">{fmtDur(end - start)}</span>
        </div>
        {entry.description && <EntryProjectLabel project={projectData} />}
      </div>
      <span
        className="cal-entry-handle cal-entry-handle-bottom"
        onMouseDown={(event) => onResizeStart(event, entry)}
      />
      <span className="cal-entry-tooltip">{tooltip}</span>
    </button>
  );
}
